"use client";

import { createContext, useContext, useEffect, useLayoutEffect, useState, type ReactNode, useCallback, useMemo } from "react";
import { SWRConfig } from "swr";
import { changeWorkspaceBoundary, invalidateWorkspaceBoundary } from "@/lib/workspace-lifetime";
import { useAuth } from "@/contexts/auth-context";
import { getWorkspaces, type Workspace } from "@/lib/workspace-api";

interface WorkspaceContextValue {
  workspaces: Workspace[]; selected: Workspace | null; loading: boolean; error: boolean;
  select: (domain: string) => void; refresh: () => void; loadMore: () => void; hasMore: boolean;
}
const Context = createContext<WorkspaceContextValue | null>(null);

// Remount on account changes: no state or cached responses cross principals.
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  return <AccountWorkspaces key={isLoading ? "verifying" : user?.id ?? "anonymous"}
    account={isLoading ? null : user?.id ?? null}>{children}</AccountWorkspaces>;
}

function AccountWorkspaces({ account, children }: { account: string | null; children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pages, setPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [version, setVersion] = useState(0);
  const [readyBoundary, setReadyBoundary] = useState<string | null>(null);
  useEffect(() => {
    if (!account) return;
    let current = true;
    setLoading(true);
    setError(false);
    // No persisted domain content or authority. Selection stays within this tab.
    async function load() {
      try {
        const rows: Workspace[] = [];
        let more = false;
        for (let page = 0; page < pages; page++) {
          const batch = await getWorkspaces(page * 100);
          if (!current || document.visibilityState === "hidden") return;
          rows.push(...batch);
          more = batch.length === 100;
          if (!more) break;
        }
        if (current) {
          const unique = [...new Map(rows.map(row => [row.domain_id, row])).values()];
          setWorkspaces(unique);
          // The UAT contract promises that an actor with exactly one current
          // workspace starts inside that boundary.  Leaving selectedId empty
          // made the UI show the no-workspace state even though the API had
          // returned one authorized scope.
          if (unique.length === 1) setSelectedId(previous => previous ?? unique[0].domain_id);
          setHasMore(more);
        }
      } catch {
        if (current) { setError(true); setWorkspaces([]); setSelectedId(null); }
      } finally { if (current) setLoading(false); }
    }
    void load();
    return () => { current = false; };
  }, [account, pages, version]);
  const selected = workspaces.find(row => row.domain_id === selectedId) ?? null;
  const identityKey = (row: Workspace | null) => row
    ? `${account}:${row.organization_id}:${row.tenant_id}:${row.domain_id}:${row.execution_epoch}:${[...row.operations].sort().join(",")}` : `${account}:unselected`;
  const select = useCallback((id: string) => {
    const row = workspaces.find(item => item.domain_id === id) ?? null;
    if (!id || row) {
      changeWorkspaceBoundary(identityKey(row), row);
      setSelectedId(id || null);
    }
  }, [workspaces, account]);
  const refresh = useCallback(() => setVersion(v => v + 1), []);
  const loadMore = useCallback(() => setPages(v => v + 1), []);
  // A new cache provider and React subtree discard prior domain state and
  // unmount polling/chat/drawers on selection change. Requests carry captured
  // scope explicitly; their responses cannot populate the new cache.
  const scopeKey = identityKey(selected);
  // Child SWR hooks also start requests in layout effects. Mount them only
  // after this boundary is installed; otherwise the parent aborts their first
  // requests and administration discovery stays failed until a manual retry.
  useLayoutEffect(() => {
    changeWorkspaceBoundary(scopeKey, selected);
    setReadyBoundary(scopeKey);
  }, [scopeKey]);
  useLayoutEffect(() => () => invalidateWorkspaceBoundary(), []);
  useEffect(() => {
    if (!account) return;
    const refreshAuthority = () => setVersion(value => value + 1);
    const quarantine = () => {
      invalidateWorkspaceBoundary(); setWorkspaces([]); setSelectedId(null); setHasMore(false);
    };
    const visibility = () => { if (document.visibilityState === "hidden") quarantine(); else refreshAuthority(); };
    const restore = (event: PageTransitionEvent) => { if (event.persisted) { quarantine(); refreshAuthority(); } };
    const timer = setInterval(refreshAuthority, 60_000);
    window.addEventListener("focus", refreshAuthority);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pageshow", restore);
    return () => { clearInterval(timer); window.removeEventListener("focus", refreshAuthority);
      document.removeEventListener("visibilitychange", visibility); window.removeEventListener("pageshow", restore); };
  }, [account]);
  const cache = useMemo(() => new Map(), [scopeKey]);
  return <Context.Provider value={{ workspaces, selected, loading, error, select, refresh, loadMore, hasMore }}>
    {readyBoundary === scopeKey && <SWRConfig key={scopeKey} value={{ provider: () => cache }}>{children}</SWRConfig>}
  </Context.Provider>;
}

export function useWorkspace() {
  const context = useContext(Context);
  if (!context) throw new Error("WorkspaceProvider is required");
  return context;
}
